/**
 * "Send a copy…" on one of this person's projects: the whole project, copied into another Pro
 * account once that person accepts (utils/projectCopies.ts). Not sharing: the copy is theirs,
 * apart from the original from then on. The dialog says what comes along, takes the address,
 * and lists the copies of this project sent so far with where each one stands.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, Check, Loader2, Send } from 'lucide-react';
import { Dialog } from './Dialog';
import type { WorkspaceFolder } from '../../utils/workspaceFolders';
import type { LibraryWorkspace } from '../../utils/workspaceLibrary';
import { getDescendantFolderIds } from '../../utils/folderTree';
import { pluralize } from '../../utils/workspaceLibrary';
import { copyErrorText, type ProjectCopy } from '../../utils/projectCopies';

export interface SendCopyDialogProps {
    open: boolean;
    project: WorkspaceFolder | null;
    folders: WorkspaceFolder[];
    workspaces: LibraryWorkspace[];
    /** This person's plan: sending is for Pro accounts */
    isPro: boolean;
    /** Copies this person has sent (every project; the dialog shows this project's) */
    sent: ProjectCopy[];
    onSend: (projectId: string, email: string) => Promise<ProjectCopy>;
    /** Takes back a copy nobody has accepted yet */
    onCancel: (copyId: string) => Promise<void>;
    onClose: () => void;
    /** The plan page, for someone not on Pro */
    onSubscription?: () => void;
}

/** Where a sent copy stands, in words */
export const copyStatusText = (copy: ProjectCopy): string => {
    switch (copy.status) {
        case 'pending': return 'Waiting for them to accept';
        case 'copying': return copy.progress ? `Being made: ${copy.progress.copiedWorkspaces} of ${copy.progress.totalWorkspaces} workspaces` : 'Being made';
        case 'done': return 'Copied';
        case 'declined': return 'Declined';
        case 'cancelled': return 'Withdrawn';
        case 'expired': return 'Ran out (30 days)';
        default: return copy.status;
    }
};

const formatDay = (ts: number): string => new Date(ts).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });

export const SendCopyDialog: React.FC<SendCopyDialogProps> = ({ open, project, folders, workspaces, isPro, sent, onSend, onCancel, onClose, onSubscription }) => {
    const [email, setEmail] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sentTo, setSentTo] = useState<string | null>(null);
    const [cancelling, setCancelling] = useState<string | null>(null);

    useEffect(() => {
        if (open) { setEmail(''); setError(null); setSentTo(null); setBusy(false); }
    }, [open, project?.id]);

    // What the copy carries: the workspaces in the project that are in the cloud, and the ones that are not
    const contents = useMemo(() => {
        if (!project) return { cloud: 0, local: 0, folders: 0 };
        const ids = getDescendantFolderIds(folders, project.id);
        ids.add(project.id);
        const inside = workspaces.filter(w => !w.isShared && !!w.folderId && ids.has(w.folderId));
        const cloud = inside.filter(w => !w.isLocal && w.syncedToCloud).length;
        return { cloud, local: inside.length - cloud, folders: ids.size - 1 };
    }, [project, folders, workspaces]);

    const copiesOfProject = useMemo(() => (project ? sent.filter(c => c.projectId === project.id) : []), [sent, project]);

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!project || busy) return;
        const address = email.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(address)) { setError('Give the email address of the account the copy is for.'); return; }
        setBusy(true);
        setError(null);
        try {
            const copy = await onSend(project.id, address);
            setSentTo(copy.toEmail || address);
            setEmail('');
        } catch (err) {
            setError(copyErrorText(err, 'The copy could not be sent. Please try again.'));
        } finally {
            setBusy(false);
        }
    };

    const cancel = async (copy: ProjectCopy) => {
        if (cancelling) return;
        setCancelling(copy.id);
        try {
            await onCancel(copy.id);
        } catch (err) {
            setError(copyErrorText(err, 'The copy could not be withdrawn.'));
        } finally {
            setCancelling(null);
        }
    };

    return (
        <Dialog
            open={open && !!project}
            title={`Send a copy of “${project?.name ?? ''}”`}
            description="The person gets a project of their own: the folders, workspaces, nodes and Ideas cards, with the media kept in the cloud. From then on the two projects are apart; what either of you changes stays on your side. To work on one project together, share it instead."
            onClose={onClose}
            busy={busy}
            footer={<button type="button" className="ns-btn ns-btn-soft ns-btn-sm" onClick={onClose} disabled={busy}>Done</button>}
        >
            <div className="space-y-4" data-send-copy-dialog>
                <p className="ns-t-small" data-send-copy-contents>
                    {pluralize(contents.cloud, 'workspace')} in the cloud{contents.folders > 0 ? ` in ${pluralize(contents.folders, 'folder')}` : ''} will be copied.
                    {contents.local > 0 && ` ${pluralize(contents.local, 'workspace')} kept only on this device ${contents.local === 1 ? 'is' : 'are'} not included.`}
                    {' '}Media that exists only on your computer is not included either.
                </p>

                {isPro ? (
                    <form onSubmit={submit} className="space-y-3" data-send-copy-form noValidate>
                        <div>
                            <label className="ns-field-label" htmlFor="send-copy-email">Their email address</label>
                            <input
                                id="send-copy-email"
                                type="email"
                                className="ns-field"
                                placeholder="name@example.com"
                                value={email}
                                onChange={(e) => { setEmail(e.target.value); setError(null); setSentTo(null); }}
                                autoComplete="off"
                                disabled={busy}
                                data-autofocus
                            />
                            <p className="ns-t-meta mt-1.5">They need a Pro account. The copy is made when they accept it, and they are told in their bell.</p>
                        </div>
                        {error && <p className="ns-note" data-tone="danger" role="alert"><AlertCircle aria-hidden="true" /><span>{error}</span></p>}
                        {sentTo && (
                            <p className="ns-note" data-tone="ok" role="status" data-send-copy-sent>
                                <Check aria-hidden="true" />
                                <span>Sent to {sentTo}. They find it under Shared with me; the copy is made once they accept.</span>
                            </p>
                        )}
                        <div className="flex justify-end">
                            <button type="submit" className="ns-btn ns-btn-primary ns-btn-sm" disabled={busy || !email.trim()}>
                                {busy ? <Loader2 size={14} className="animate-spin" aria-hidden="true" /> : <Send size={14} aria-hidden="true" />}
                                Send a copy
                            </button>
                        </div>
                    </form>
                ) : (
                    <div className="ns-note block p-4" data-tone="warn" data-send-copy-pro>
                        <p>Sending a copy of a project is for Pro accounts, on both sides.</p>
                        {onSubscription && <button type="button" className="ns-btn ns-btn-primary ns-btn-sm mt-3" onClick={onSubscription}>See Pro plans</button>}
                    </div>
                )}

                {copiesOfProject.length > 0 && (
                    <section aria-labelledby="send-copy-sent-heading">
                        <h3 id="send-copy-sent-heading" className="ns-t-label mb-2">Copies sent</h3>
                        <ul className="flex flex-col gap-1.5" data-send-copy-list>
                            {copiesOfProject.map(copy => (
                                <li key={copy.id} className="ns-well flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2" data-sent-copy={copy.id} data-copy-status={copy.status}>
                                    <span className="min-w-0 flex-1">
                                        <span className="block text-sm truncate">{copy.toEmail}</span>
                                        <span className="block ns-t-meta">{copyStatusText(copy)} · {formatDay(copy.createdAt)}</span>
                                    </span>
                                    {copy.status === 'pending' && (
                                        <button type="button" className="ns-btn ns-btn-ghost ns-btn-xs ns-btn-flat" disabled={!!cancelling} onClick={() => { void cancel(copy); }}>
                                            {cancelling === copy.id ? <Loader2 size={12} className="animate-spin" aria-hidden="true" /> : null}
                                            Withdraw
                                        </button>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </section>
                )}
            </div>
        </Dialog>
    );
};
